/**
 * @coeldery/bottom-nav —— **通用底部導航欄（社群／會員 icon 集）**
 *
 * 血緣：`@coeldery/role-tab-bar@1.0.0`（同族：可配置 tabs 嘅通用底部導航，帶 SMS 物流 icon）。
 * 為何另開模組（R1／R3）：role-tab-bar 嘅 `icons.tsx` 只有物流 icon（箱／手推車／倉租…），
 *   本產品要嘅係**社群／會員 icon**（優惠／資訊／卡片／廣告／設定）→ 依模組自身 `notFor`
 *   「要其他 icon 就自己砌或另開模組」新建，並留 `derivedFrom` 血緣（回流中央庫）。
 *
 * ⭐ 同 `role-tab-bar` 一樣：**唔識路由**（`onSelect(id)` 交返 app）；顏色一律 CSS var；
 *   文字一律 `t(labelKey)`（三語由 app 字典提供）。
 * ⚠️ 5 個 tab 用 **12px 標籤**（比 4-tab 版嘅 18px 細，遷就 5 欄）；icon 26px；bar 高 `var(--bottombar-h, 72px)`。
 */
import { useTranslation } from 'react-i18next';
import { NavIconSvg, type NavIcon } from './icons.tsx';

/** 重匯出 icon 型別（方便呼叫者砌 tabs） */
export type { NavIcon } from './icons.tsx';

export interface TabDef {
  /** 穩定 id（app 層用嚟對路由） */
  id: string;
  /** i18n key（例 `tab.benefits`） */
  labelKey: string;
  /** 內置 icon 名 */
  icon: NavIcon;
}

export interface BottomNavProps {
  /** tabs（次序 ＝ 畫面次序；一般 3–5 個） */
  tabs: readonly TabDef[];
  /** 而家 active 嘅 id */
  activeId: string;
  /** 撳 tab（呼叫者通常 `navigate(pathOf(id))`） */
  onSelect: (id: string) => void;
}

export default function BottomNav({ tabs, activeId, onSelect }: BottomNavProps) {
  const { t } = useTranslation();

  return (
    <nav
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        minHeight: 'var(--bottombar-h, 72px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: 'var(--color-card)',
        borderTop: '1px solid var(--color-divider)',
        zIndex: 100,
        boxSizing: 'border-box',
      }}
      aria-label={t('tab.aria')}
    >
      {tabs.map((tb) => {
        const active = tb.id === activeId;
        return (
          <button
            key={tb.id}
            type="button"
            onClick={() => onSelect(tb.id)}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '6px 2px',
              position: 'relative',
              fontFamily: 'inherit',
            }}
          >
            {active && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '18%',
                  right: '18%',
                  height: '3px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '0 0 3px 3px',
                }}
              />
            )}
            <NavIconSvg name={tb.icon} active={active} />
            <span
              style={{
                fontSize: 'var(--font-size-label, 1.125rem)',
                fontWeight: active ? 800 : 600,
                color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                lineHeight: 1.1,
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t(tb.labelKey)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** 內容區底部留白（唔好畀 nav bar 遮住最後一行） */
export const NAV_BAR_SPACER: React.CSSProperties = {
  height: 'calc(var(--bottombar-h, 72px) + env(safe-area-inset-bottom, 0px) + 12px)',
};
